import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Button,
  Flex,
  ActionButton,
  TextField
} from '@adobe/react-spectrum';
import Add from '@spectrum-icons/workflow/Add';
import Remove from '@spectrum-icons/workflow/Remove';
import { FormField } from '../../store/slice';
import styles from '../FormField.module.scss';
import LDAPSearchField from '../../../shared/LDAPSearch/LDAPSearchField';

interface RepeatableFieldProps {
  field: FormField;
  value: any[];
  onChange: (fieldName: string, value: any[]) => void;
}

// Props for the content component where we know itemTemplate exists
interface RepeatableFieldContentProps extends RepeatableFieldProps {
  field: FormField & { itemTemplate: NonNullable<FormField['itemTemplate']> };
}

// Separate component for the repeatable field content
const RepeatableFieldContent: React.FC<RepeatableFieldContentProps> = ({
  field,
  value = [],
  onChange
}) => {
  const [items, setItems] = useState<any[]>(
    Array.isArray(value) && value.length > 0 ? value : ['']
  );

  useEffect(() => {
    onChange(field.name, items);
  }, [items, field.name, onChange]);

  const handleAddItem = () => {
    setItems([...items, '']);
  };

  const handleRemoveItem = (index: number) => {
    if (field.minItems && items.length <= field.minItems) {
      return;
    }

    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemChange = (index: number, newValue: string) => {
    const newItems = [...items];
    newItems[index] = newValue;
    setItems(newItems);
  };

  const showRemoveButton = !field.minItems || items.length > field.minItems;
  const { itemTemplate } = field;
  const fieldTestId = `repeatable-field-${field.name
    .replace(/[^a-zA-Z0-9]/g, '-')
    .toLowerCase()}`;

  return (
    <View
      UNSAFE_className={styles.repeatableFieldContainer}
      data-testid={fieldTestId}
    >
      {items.map((item, index) => (
        <Flex
          key={index}
          UNSAFE_className={styles.repeatableField}
          alignItems="end"
          data-testid={`${fieldTestId}-item-${index}`}
        >
          {itemTemplate.type === 'ldap' ? (
            <div className={styles.ldapFieldWrapper}>
              <label className={styles.fieldLabel}>{itemTemplate.label}</label>
              <LDAPSearchField
                label=""
                value={item}
                onChange={val => handleItemChange(index, val)}
                isRequired={itemTemplate.required}
                cssClasses={{
                  ldapTextField: styles.ldapTextField,
                  selectedTag: styles.selectedTag,
                  tagRemoveButton: styles.tagRemoveButton,
                  ldapDropdown: styles.ldapDropdown
                }}
                data-testid={`${fieldTestId}-ldap-field-${index}`}
              />
            </div>
          ) : (
            <TextField
              key={index}
              label={itemTemplate.label}
              UNSAFE_className={styles.textField}
              value={item || ''}
              onChange={value => handleItemChange(index, value)}
              isRequired={itemTemplate.required}
              data-testid={`${fieldTestId}-text-field-${index}`}
            />
          )}

          {showRemoveButton && (
            <ActionButton
              marginStart="size-100"
              onPress={() => handleRemoveItem(index)}
              data-testid={`${fieldTestId}-remove-button-${index}`}
            >
              <Remove data-testid={`${fieldTestId}-remove-icon-${index}`} />
            </ActionButton>
          )}
        </Flex>
      ))}

      <Button
        variant="secondary"
        onPress={handleAddItem}
        marginTop="size-100"
        data-testid={`${fieldTestId}-add-button`}
      >
        <Add data-testid={`${fieldTestId}-add-icon`} />
        <Text data-testid={`${fieldTestId}-add-text`}>
          Add {itemTemplate.label}
        </Text>
      </Button>
    </View>
  );
};

// Main component that handles the template check
export const RepeatableFieldComponent: React.FC<
  RepeatableFieldProps
> = props => {
  if (!props.field.itemTemplate) {
    return <Text>Item template is missing for repeatable field</Text>;
  }

  // We can safely cast here because we've checked that itemTemplate exists
  return <RepeatableFieldContent {...(props as RepeatableFieldContentProps)} />;
};
